import {Component, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {CustomToastService} from "../../../../@core/utils/custom-toast.service";

import {PFLICHTFELDMARKER} from "../../../../../assets/constants/constants";
import {TranslateService} from "@ngx-translate/core";
import {TerminationReasonType} from "../../../../models/student-ticket/termination/TerminationReasonType";
import {StudentTicketService} from "../../../../@core/services/student-ticket/student-ticket.service";
import {EnumTranslationHelperService} from "../../../../@core/utils/enum-translation-helper.service";
import {TerminationRequestDTO} from "../../../../models/student-ticket/termination/TerminationRequestDTO";
import {EMPTY} from "rxjs";
import {TerminationConstraintsDTO} from "../../../../models/student-ticket/termination/TerminationConstraintsDTO";
import {TerminationDateInputType} from "../../../../models/student-ticket/termination/TerminationDateInputType";
import {DatePipe} from "@angular/common";

@Component({
    selector: 'app-termination-form-modal',
    templateUrl: './termination-form-modal.component.html',
    styleUrls: ['./termination-form-modal.component.scss'],
    standalone: false
})
export class TerminationFormModalComponent implements OnInit {

  fetch_error: string;
  validation_error: string;
  terminationForm: UntypedFormGroup = {} as UntypedFormGroup;

  terminationReasonTypeLabels: [];
  terminationReasonTypeTexts: [];
  currentType: TerminationReasonType;
  currentTypeText : string;

  ticketId: string; // uuid
  earliestTerminationDate : Date;
  latestTerminationDate : Date;
  allowedTerminationReasons: TerminationReasonType[] = [];
  dateInputType : TerminationDateInputType = TerminationDateInputType.DATEPICKER_DAY;

  finalTerminationDate: Date;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly fb: FormBuilder,
    private readonly customToastService: CustomToastService,
    private readonly enumTranslateService: EnumTranslationHelperService,
    private readonly studentTicketService: StudentTicketService,
    public translateService: TranslateService,
    private readonly datePipe: DatePipe
  ) {
  }

  ngOnInit(): void {

    this.studentTicketService.getTerminationConstraints(this.ticketId)
      .subscribe({
        next: (response: TerminationConstraintsDTO) => {

          this.earliestTerminationDate = response.earliestTerminationDate;
          this.latestTerminationDate = response.latestTerminationDate;
          this.allowedTerminationReasons = response.allowedTerminationReasons;
          this.dateInputType = response.dateInputType;

          this.getTranslationsForLabel().then(() => {
            this.getTranslationsForReasonText().then(() => {

              this.fetch_error = this.translateService.instant('TERMINATE_TICKET.ERROR_FETCHING_TERMINATION_CONSTRAINTS');
              this.validation_error = this.translateService.instant('TERMINATE_TICKET.REQUIRED_FIELD_MISSING');

              this.terminationForm = this.fb.group({
                terminationDate: [undefined],
                terminationType: ['ANTRAGSTELLER'],
                terminationReason: [undefined]
              });

              this.currentTypeChanged(); // trigger that text "Kuendigung durch Antragsteller:in" is patched

            });
          });

        },
        error: () => {
          this.customToastService.showError(this.fetch_error);
          this.activeModal.dismiss();
          return EMPTY;
        }
      });

  }

  setFinalTerminationDate() {
    if( this.terminationForm.get('terminationDate').value) {
      if(this.dateInputType === TerminationDateInputType.DATEPICKER_DAY) {
        this.finalTerminationDate = this.getNoonDate(this.terminationForm?.get('terminationDate')?.value);
      }
      else if(this.dateInputType === TerminationDateInputType.DATEPICKER_MONTH) {
        const strMonthYear = this.terminationForm?.get('terminationDate')?.value;
        this.finalTerminationDate = this.getDateEndOfMonth(strMonthYear);
      }
      else {
        this.finalTerminationDate = undefined;
      }
    } else {
      this.finalTerminationDate = undefined;
    }
  }

  getDateEndOfMonth(strMonthYear: string | undefined): Date {
    if(!strMonthYear){
      return undefined;
    }
    const [year, month] = strMonthYear.split('-').map(Number);
    // The 12:00 is needed for DTO, because JSON stringify and UTC shift etc
    return new Date(year, month, 0, 12, 0, 0, 0);
  }

  getNoonDate(date: Date | undefined): Date {
    if (!date) {
      return undefined;
    }
    const noonDate = new Date(date);
    noonDate.setHours(12, 0, 0, 0);
    return noonDate;
  }

  getTranslationsForLabel():Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.enumTranslateService
        .getTranslationObservableForEnumByValue<typeof TerminationReasonType, string>(
          TerminationReasonType,
          'TERMINATE_TICKET.REASON_TYPES_LABELS.',
          (it : TerminationReasonType) => this.allowedTerminationReasons.includes(it),
        )
        .subscribe(temp => {
          this.terminationReasonTypeLabels = temp;
          resolve(true);
        });
    });
  }

  getTranslationsForReasonText():Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.enumTranslateService
        .getTranslationObservableForEnumByValue<typeof TerminationReasonType,string>(
          TerminationReasonType,
          'TERMINATE_TICKET.REASON_TYPES_TEXTS.',
          (it : TerminationReasonType) => this.allowedTerminationReasons.includes(it),
        )
        .subscribe(temp => {
          this.terminationReasonTypeTexts =  temp;
          resolve(true)
        });
    });
  }

  getFormValue(field: string): any {
    return this.terminationForm.get(field).value;
  }

  currentTypeChanged() {
    this.terminationForm.get('terminationReason').setValue(undefined);
    this.currentType = this.terminationForm?.get('terminationType')?.value;
    if(this.currentType){
      this.currentTypeText = this.terminationReasonTypeTexts.find(it=> it['value'] === this.currentType)['display'];
    }
    else {
      this.currentTypeText = undefined;
    }
    this.updateTerminationReasonValidation();
  }

  updateTerminationReasonValidation(){
    if(this.currentType === TerminationReasonType.SONSTIGES){
      this.terminationForm.get('terminationReason').addValidators([Validators.required]);
    }
    else {
      this.terminationForm.get('terminationReason').removeValidators([Validators.required]);
    }
    this.terminationForm.markAllAsTouched();
    this.terminationForm.get('terminationReason').updateValueAndValidity();
  }

  onNoClick(): void {
    this.activeModal.dismiss();
  }

  onSubmit(): void {
    this.terminationForm.get('terminationDate').addValidators([Validators.required]);
    this.terminationForm.get('terminationDate').updateValueAndValidity();
    this.terminationForm.markAllAsTouched();
    if(!this.terminationForm.valid){
      this.customToastService.showError(this.validation_error);
      return;
    }
    this.activeModal.close({
      terminationDate: this.finalTerminationDate,
      terminationReason: this.getFormValue('terminationReason') ?? this.currentTypeText,
      terminationReasonType: this.getFormValue('terminationType'),
    } as TerminationRequestDTO);
  }

  isFormReady(): boolean {
    return typeof this.terminationForm?.get === 'function';
  }

  setEarliestTerminationDate(): void {
    if(this.dateInputType === TerminationDateInputType.DATEPICKER_DAY) {
      this.terminationForm.get('terminationDate').patchValue(this.datePipe.transform(this.earliestTerminationDate, 'yyyy-MM-dd'));
    }
    else if(this.dateInputType === TerminationDateInputType.DATEPICKER_MONTH) {
      this.terminationForm.get('terminationDate').patchValue(this.datePipe.transform(this.earliestTerminationDate, 'yyyy-MM'));
    }
    this.setFinalTerminationDate();
  }

  protected readonly Date = Date;
  protected readonly PFLICHTFELDMARKER = PFLICHTFELDMARKER;
  protected readonly TerminationDateInputType = TerminationDateInputType;
}
